import { TaggedError } from "better-result";

import { isAbortOrCanceledError } from "./abort-signals";

export interface UserFacingErrorInfo {
  userFacingMessage: string;
}

interface UserFacingErrorProps extends Record<string, unknown>, UserFacingErrorInfo {
  message: string;
  cause?: unknown;
}

export class UserFacingError extends TaggedError("UserFacingError")<UserFacingErrorProps>() {}

export class ScopedError extends Error {
  name = "ScopedError";
  label: string;
  durationMs: number;
  metadata?: Record<string, unknown>;
  userFacingMessage?: string;

  constructor(
    props: {
      label: string;
      durationMs: number;
      metadata?: Record<string, unknown>;
      userFacingErrorInfo?: Partial<UserFacingErrorInfo>;
    },
    cause?: unknown
  ) {
    const scopedMessage = `[scoped:${props.label}] ${getErrorMessage(cause)}`;
    super(scopedMessage, { cause });
    const causeUserFacingErrorInfo = getUserFacingErrorInfo(cause);
    const userFacingErrorInfo = {
      ...causeUserFacingErrorInfo,
      ...props.userFacingErrorInfo,
    };

    this.label = props.label;
    this.durationMs = props.durationMs;
    this.metadata =
      cause instanceof ScopedError ? { ...cause.metadata, ...props.metadata } : props.metadata;
    this.userFacingMessage = userFacingErrorInfo.userFacingMessage;
    this.stack = cause instanceof Error ? cause.stack : this.stack;
  }
}

export class ScopedAbortError extends ScopedError {
  name = "ScopedAbortError";
}

function extractMessageFromObject(object: unknown) {
  const messageKeys = ["message", "error"];

  for (const key of messageKeys) {
    if (
      typeof object === "object" &&
      object !== null &&
      key in object &&
      typeof object[key as keyof typeof object] === "string"
    ) {
      return object[key as keyof typeof object];
    }
  }
}

const MAX_DEPTH_CHECK = 5;

function extractErrorFromObject(object: unknown, depth = 0) {
  // extra safety check to avoid going too deep into an object in a runaway loop
  if (depth > MAX_DEPTH_CHECK) {
    return;
  }

  if (object instanceof Error) {
    return object.message;
  }

  const messageInObject = extractMessageFromObject(object);

  if (messageInObject) {
    return messageInObject;
  }

  if (typeof object === "object" && object !== null) {
    if ("error" in object) {
      if (typeof object.error === "object" && object.error !== null) {
        return extractErrorFromObject(object.error, depth + 1);
      }

      if (typeof object.error === "string" || typeof object.error === "number") {
        return String(object.error);
      }
    }

    if ("data" in object && typeof object.data === "object" && object.data !== null) {
      return extractErrorFromObject(object.data, depth + 1);
    }
  }
}

export function getErrorMessage(error: unknown) {
  try {
    if (typeof error === "number" || typeof error === "boolean" || typeof error === "symbol") {
      return String(error);
    }

    if (typeof error === "string" && error.length > 0) {
      return error;
    }

    const message = extractErrorFromObject(error);

    if (message) {
      return message;
    }
  } catch {
    // do nothing
  }

  return "unknown error";
}

export function getErrorScopedLabel(error: unknown) {
  if (error instanceof ScopedError) {
    return error.label;
  }
}

export function getUserFacingErrorInfo(error: unknown): UserFacingErrorInfo | undefined {
  if (UserFacingError.is(error)) {
    return {
      userFacingMessage: error.userFacingMessage,
    };
  }

  if (error instanceof ScopedError && error.userFacingMessage) {
    return {
      userFacingMessage: error.userFacingMessage,
    };
  }

  if (isUserFacingErrorLike(error)) {
    return error;
  }
}

interface ScopedOptions<M extends Record<string, unknown>> extends Partial<UserFacingErrorInfo> {
  getMetadata?: () => M;
}

export async function $scoped<R, M extends Record<string, unknown>>(
  label: string,
  fn: Promise<R> | (() => Promise<R>),
  options?: ScopedOptions<M>
) {
  const start = performance.now();

  const metadata = options?.getMetadata?.();

  try {
    const promiseValue = typeof fn === "function" ? fn() : fn;

    return await promiseValue;
  } catch (error) {
    const durationMs = Math.ceil(performance.now() - start);

    if (isAbortOrCanceledError(error)) {
      throw new ScopedAbortError({ label, durationMs, metadata }, error);
    }

    throw new ScopedError(
      {
        label,
        durationMs,
        metadata,
        userFacingErrorInfo: getScopedUserFacingErrorInfo(options ?? {}, error),
      },
      error
    );
  }
}

export type BaseScopedTaskEventParams = {
  label: string;
  status: "start" | "finish" | "error" | "cancel";
  duration_ms?: number;
  error_message?: string;
};

interface ScopedTaskOptions<
  R,
  M extends Record<string, unknown>,
  MResult extends Record<string, unknown> = Record<string, never>,
> extends Partial<UserFacingErrorInfo> {
  getMetadata: () => M;
  getResultMetadata?: (result: R) => MResult;
  trackEvent: (params: BaseScopedTaskEventParams & M & Record<string, unknown>) => void;
}

// similar to $scoped, but with additional tracking of the task
export async function $scopedTask<
  R,
  M extends Record<string, unknown>,
  MResult extends Record<string, unknown> = Record<string, never>,
>(label: string, fn: () => Promise<R>, options: ScopedTaskOptions<R, M, MResult>) {
  const start = performance.now();

  const metadata = options?.getMetadata?.();

  try {
    options.trackEvent({ label, status: "start", ...metadata });

    const promiseValue = typeof fn === "function" ? fn() : fn;

    const result = await promiseValue;

    options.trackEvent({
      label,
      status: "finish",
      ...metadata,
      duration_ms: Math.ceil(performance.now() - start),
      ...(options.getResultMetadata ? options.getResultMetadata(result) : {}),
    });

    return result;
  } catch (error) {
    const durationMs = Math.ceil(performance.now() - start);

    const errorScopedLabel = getErrorScopedLabel(error);
    const errorLabel = errorScopedLabel ? `${label} (${errorScopedLabel})` : label;

    if (isAbortOrCanceledError(error)) {
      options.trackEvent({
        label: errorLabel,
        status: "cancel",
        duration_ms: durationMs,
        ...metadata,
      });
      throw new ScopedAbortError({ label, durationMs, metadata }, error);
    }

    const userFacingErrorInfo = getScopedUserFacingErrorInfo(options, error);

    options.trackEvent({
      label: errorLabel,
      status: "error",
      duration_ms: durationMs,
      error_message: getErrorMessage(error),
      ...metadata,
    });

    throw new ScopedError({ label: errorLabel, durationMs, metadata, userFacingErrorInfo }, error);
  }
}

export function $createBoundTask<M extends Record<string, unknown>>(
  options: Pick<
    ScopedTaskOptions<never, M, never>,
    "getMetadata" | "trackEvent" | "userFacingMessage"
  >
) {
  return <R>(label: string, fn: () => Promise<R>) =>
    $scopedTask<R, M, Record<string, never>>(label, fn, options);
}

function getScopedUserFacingErrorInfo(
  options: Partial<UserFacingErrorInfo>,
  error: unknown
): UserFacingErrorInfo | undefined {
  const errorInfo = getUserFacingErrorInfo(error);
  const userFacingMessage = options.userFacingMessage ?? errorInfo?.userFacingMessage;

  if (!userFacingMessage) {
    return;
  }

  return {
    userFacingMessage,
  };
}

function isUserFacingErrorLike(error: unknown): error is UserFacingErrorInfo {
  return (
    typeof error === "object" &&
    error !== null &&
    "userFacingMessage" in error &&
    typeof error.userFacingMessage === "string" &&
    error.userFacingMessage.length > 0
  );
}
